/* eslint complexity: 0 */
import {
  cohort_state_class,
  cohort_state_from_status,
  empty_training_value,
  normalise_control,
  normalise_course_drawer,
  normalise_training_result,
  pass_tone_from_percent,
  result_badge_class,
  score_tone_from_percent
} from './training_program_helpers';

function parse_percent(value) {
  const parsed_value = Number.parseInt(String(value).replace('%', ''), 10) || 0;
  return parsed_value;
}

function parse_count(value) {
  const parsed_value = Number.parseInt(value, 10) || 0;
  return parsed_value;
}

function course_form_values(row, form_override) {
  const form = form_override || {};
  const assigned_people = parse_count(form.assigned_people);
  const default_completed = Math.round((row.completion / 100) * (assigned_people || 24));
  const completed_people = parse_count(form.completed_people) || default_completed;
  const values = {
    form,
    audience: form.audience || row.audience,
    category: form.category || row.category,
    assigned_people,
    completed_people,
    pass_rate: parse_percent(row.pass),
    section_key: (form.category || row.category || 'Other').replace(/[^A-Za-z0-9]+/g, '-').toUpperCase()
  };
  return values;
}

function course_pass_note(pass_rate) {
  if (pass_rate >= 90) return 'Good';
  if (pass_rate >= 75) return 'Fair';
  return 'Needs attention';
}

function course_index_label(matrix_rows, course_name) {
  const course_number = String(matrix_rows.findIndex((item) => item.course === course_name) + 1).padStart(2, '0');
  return course_number;
}

function course_linked_items(row, values) {
  const control_value = normalise_control(values.form.linked_control || row.control);
  const linked_items = [
    {
      icon: 'bi bi-file-earmark-text',
      label: 'Policy',
      value: values.form.linked_policy || row.policy || 'Policy not linked'
    },
    {
      icon: 'bi bi-people',
      label: 'Role / audience',
      value: values.audience
    },
    {
      icon: 'bi bi-collection',
      label: 'Evidence vault',
      value: `${values.completed_people ? 1 : 0} certificates`
    },
    {
      icon: 'bi bi-tag',
      label: 'Category',
      value: values.category
    }
  ];

  if (control_value === 'â€”') return linked_items;

  const control_items = [
    linked_items[0],
    linked_items[1],
    {
      icon: 'bi bi-shield-check',
      label: 'Control',
      value: control_value
    },
    linked_items[2],
    linked_items[3]
  ];
  return control_items;
}

export function build_course_drawer_payload(context, row, form_override = null) {
  const values = course_form_values(row, form_override);
  const state = cohort_state_from_status(row.status.label);
  const outstanding = Math.max(
    values.assigned_people - values.completed_people,
    Math.round((100 - row.completion) / 4),
    0
  );
  const drawer = {
    section: `Course Â· TRN-${course_index_label(context.matrix_rows, row.course)} Â· ${values.section_key}`,
    title: row.course,
    subtitle: `Audience ${values.audience} Â· ${values.form.validity || '12 months'} validity`,
    status: row.status.label,
    status_class: row.status.class,
    completion: row.completion,
    completion_tone: score_tone_from_percent(row.completion),
    pass_rate: values.pass_rate,
    pass_tone: pass_tone_from_percent(values.pass_rate),
    pass_note: course_pass_note(values.pass_rate),
    outstanding,
    cohort_expiry: values.form.cohort_expiry || 'Review due',
    cohort_state: state,
    cohort_state_class: cohort_state_class(state),
    linked_to: course_linked_items(row, values),
    triggered_items: [],
    results: values.completed_people
      ? [{
          title: `${values.audience} cohort`,
          subtitle: `${values.completed_people}/${values.assigned_people || values.completed_people} completed`,
          status: row.status.label,
          status_class: row.status.class,
          icon: 'bi bi-mortarboard'
        }]
      : []
  };
  return drawer;
}

function status_meta(context, label) {
  const status_value =
    Reflect.get(context.status_meta_map || {}, label) ||
    Reflect.get(context.status_meta_map || {}, 'On track');
  return status_value;
}

export function create_course_state(context) {
  const course_name = context.course_form.course.trim();
  if (!course_name) return null;

  const assigned_people = parse_count(context.course_form.assigned_people);
  const completed_people = parse_count(context.course_form.completed_people);
  const completion = assigned_people > 0
    ? Math.max(0, Math.min(100, Math.round((completed_people / assigned_people) * 100)))
    : 0;
  const pass_rate = parse_count(context.course_form.pass_rate);
  const course_status_meta = status_meta(context, context.course_form.status);
  const row = {
    course: course_name,
    category: context.course_form.category,
    audience: context.course_form.audience,
    policy: context.course_form.linked_policy || 'Policy to be linked',
    control: context.course_form.linked_control || 'â€”',
    completion,
    pass: pass_rate ? `${pass_rate}%` : 'â€”',
    status: {
      label: context.course_form.status,
      class: course_status_meta.badge_class
    },
    accent: course_status_meta.accent
  };
  const drawer = normalise_course_drawer(build_course_drawer_payload(context, row, context.course_form));
  return { row, drawer, course_name };
}

function result_row_from_form(form) {
  const employee = form.employee.trim();
  const course = form.course.trim();
  if (!employee || !course) return null;

  const row = normalise_training_result({
    employee,
    dept: form.department,
    course,
    result: form.result,
    score: form.score.trim() ? `${form.score.trim()}%` : 'â€”',
    completed: empty_training_value(form.completed),
    certificate: empty_training_value(form.certificate),
    record_status: form.status,
    status: {
      label: form.result,
      class: result_badge_class(form.result)
    }
  });
  return row;
}

export function create_result_state(form) {
  const result_row = result_row_from_form(form);
  return result_row;
}
